import {Button} from "../ui/Button";
import {BaseModal, ModalCloseButton} from "../modals/ModalBase";
import {LucideGem, LucideRocket, LucideShoppingCart} from "lucide-react";
import React from "react";
import {Typography} from "../ui/Typography";
import {IconData} from "../../data/icons";
import { useCreditModal } from "../../context/CreditModalContext";


export interface IconSummaryProps {
    isOpen: boolean;
    onClose: () => void;
    iconData : IconData;
}

export const IconSummaryPopup = ({isOpen, onClose, iconData}: IconSummaryProps) => {
    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            containerClassName="bg-surface-900 w-full max-w-5xl rounded-2xl shadow-2xl grid grid-cols-1 lg:grid-cols-2 relative 
            transform scale-95 transition-all duration-300 ease-out"
        >

            <ModalCloseButton onClose={onClose}/>

            <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center items-center bg-surface-900 rounded-l-2xl border-r border-surface-700">
                
                <div id="icon-canvas"
                    className="w-full aspect-square rounded-xl flex items-center justify-center transition-colors duration-200 checkerboard-bg">
                    
                    <img alt={iconData.name} src={iconData.previewImage}/>
                    
                </div>
            </div>

            <div className="p-6 sm:p-8 lg:p-10 flex flex-col space-y-6">
                
                <Typography as="h2" variant="h2">{iconData.name}.</Typography> {/*name*/}
                <Typography as="p" variant="p">{iconData.name}.</Typography> {/*description*/}

                <div className="flex flex-wrap gap-2">
                    <Tag name="arrow"/>
                    <Tag name="direction"/>
                    <Tag name="next"/>
                    <Tag name="forward"/>
                </div>
                
                <div className="space-y-2">
                    <Typography as="label" variant="label">Format</Typography>
                    
                    <div id="format-selector"
                        className="relative w-full h-12 bg-surface-950 rounded-xl p-1 flex items-center">

                        <div id="format-slider" 
                             className="absolute h-10 bg-surface-300 rounded-lg shadow-sm transition-all duration-300 ease-in-out"/>
                        
                        <FileFormatButton format="png" />
                        <FileFormatButton format="svg" />

                    </div>
                </div>
                
                <SizeSelection />

                <div className="flex-grow" />

                <div className="flex justify-end items-stretch">
                    <Typography
                        as="h2"
                        variant="h2"
                        className="flex gap-2 py-2 mt-8"
                    >
                        <LucideGem className="mr-2 text-brand-500 w-8 h-8"/>
                        {iconData.price} credit
                    </Typography>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_3fr] gap-3 sm:gap-4">
                    <Button intent="secondary" size="large" className="bg-[transparent]">
                        <LucideShoppingCart className="mr-3"/>Add
                    </Button>

                    <Button intent="primary" size="large" glow={true} hover={true}>
                        <LucideRocket  className="mr-3"/>Buy Now
                    </Button>
                </div>

            </div>
        </BaseModal>
    );
};

const Tag = ({name}: { name: string }) => {
    return (
        <a href="#"
           className="px-3 py-1 bg-surface-950 text-surface-400 text-sm font-medium rounded-full hover:bg-surface-700 transition-colors"
        >
            {name}
        </a>
    );
};

const FileFormatButton = ({format}: { format: string }) => {
    const {openModal} = useCreditModal();
    return (
        <button
            data-format={format}
            className="format-btn flex-1 h-full z-10 text-surface-200 font-semibold transition-colors"
            onClick={() => openModal()}
        >
            {format.toUpperCase()}
        </button>
    );
};

{/* if not purchased, set isActive to false --todo*/}
{/* hide the SizeSelection component when SVG is selected --todo*/}
const SizeSelection = () => {
    return (
        <div id="size-selector-wrapper" className="transition-all duration-300 ease-in-out opacity-100 max-h-[500px] overflow-hidden">
            <Typography as="label" variant="label">Size</Typography>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ImageSizeOption size="128" isActive={true} />
                <ImageSizeOption size="256" isActive={true} />
                <ImageSizeOption size="512" isActive={true} />
                <ImageSizeOption size="1024" isActive={false} />
            </div>
        </div>
    );
};

interface ImageSizeOption {
    size :string;
    isActive: boolean;
}

const ImageSizeOption = ({size, isActive}: ImageSizeOption) => {
    return (
        <button
            data-size={size}
            className={`size-btn p-3 text-center font-semibold border-2 rounded-lg transition-colors  
            ${isActive ? 'border-brand-700 text-surface-100 hover:border-brand-300 hover:text-brand-300' 
                : 'border-surface-700 text-surface-800'}`}
        >
            {size}px
        </button>
    );
};


